import { AfterViewInit, Component, Input } from '@angular/core';
import { AppConstants } from "@core/constants";

declare let Bokeh_v373: any;

@Component({
  selector: 'app-heatmap',
  templateUrl: './heatmap.component.html',
  styleUrls: ['./heatmap.component.scss']
})
export class HeatmapComponent implements AfterViewInit {
  @Input() public config: any;

  public getBackgroundTextColorBasedOnTheme(theme: string | null): any {
    let backgroundColor: string;
    let textColor: string;
    let tickColor: string;

    if (theme === AppConstants.THEME.DARK) {
      backgroundColor = '#303030';
      textColor = '#ffffff';
      tickColor = '#737373';
    } else {
      backgroundColor = '#f9f9f9';
      textColor = '#4D4D4D';
      tickColor = '#4D4D4D';
    }

    return {
      backgroundColor,
      textColor,
      tickColor,
    };
  }

  public ngAfterViewInit(): void {
    this.config.forEach((data: any, index: number) => {
      this.createHeatmap(data, index, this.getBackgroundTextColorBasedOnTheme(localStorage.getItem('theme')));
    });
  }

  public createHeatmap(config: any, index: number, colorObject: any) {
    try {
      // Calculate responsive dimensions
      const containerWidth = document.getElementById(`heatmap-${index}`)?.clientWidth || window.innerWidth;
      const baseSize = Math.min(containerWidth * 0.8, 800); // Max 800px, min 80% of container
      const plotWidth = Math.max(baseSize, 400); // Minimum 400px
      const plotHeight = Math.max(baseSize * 0.9, 400); // Slightly shorter than width

      // Data for the heatmap
      const data = config;

      // Define colors
      const colors = ["#4CAF50", "#FFC107", "#FF5252"];

      // Create a responsive plot with adjusted ranges for 6x6 grid
      const p = new Bokeh_v373.Plotting.figure({
        width: plotWidth,
        height: plotHeight,
        min_width: 300,  // Minimum width for mobile
        max_width: 900,  // Maximum width for large screens
        sizing_mode: "scale_width", // This makes it responsive
        x_range: [-2.4, 8],  // Adjusted for 6x6 grid
        y_range: [-1.5, 7],  // Adjusted for 6x6 grid
        tools: "",
        toolbar_location: null,
        outline_line_color: colorObject.backgroundColor,         // Dark outline
        background_fill_color: colorObject.backgroundColor,      // Black background for the plot area
        border_fill_color: colorObject.backgroundColor,

        min_border_left: Math.max(plotWidth * 0.03, 20),   // Responsive margins
        min_border_bottom: Math.max(plotHeight * 0.15, 100),
        min_border_right: 0
      });

      // Configure axis labels with responsive font sizes
      const baseFontSize = Math.max(plotWidth / 60, 10);
      p.xaxis.axis_label = data.x_label + '\n\n' + data.description;
      p.yaxis.axis_label = data.y_label;
      p.xaxis.axis_label_text_font_size = `${baseFontSize}px`;
      p.yaxis.axis_label_text_font_size = `${baseFontSize}px`;
      p.xaxis.axis_label_text_color = colorObject.textColor;
      p.yaxis.axis_label_text_color = colorObject.textColor;
      p.xaxis.axis_label_standoff = 10;
      p.yaxis.axis_label_standoff = -20;  // Moved y-axis label a bit further from the axis

      // Create colored rectangles for each cell
      for (let i = 0; i < 6; i++) {
        for (let j = 0; j < 6; j++) {
          p.rect({
            x: j,
            y: 5 - i, // Adjusted for 6x6 grid (was 7-i for 8x8)
            width: 1,
            height: 1,
            fill_color: colors[data.colors[i][j]],
            line_color: "#B2B2B2",
            line_width: 1,
          });
        }
      }

      // Add vertical color bar on the right side - matching heatmap height exactly
      const barWidth = 0.2;
      const barX = 6.3;

      // Red segment (top third) - spans from y=3.5 to y=5.5
      p.rect({
        x: barX,
        y: 4.5,     // Center position
        width: barWidth,
        height: 2,   // Height of 2 units (from 3.5 to 5.5)
        fill_color: colors[2], // Red
        line_color: "#B2B2B2",
        line_width: 1
      });

      // Yellow segment (middle third) - spans from y=1.5 to y=3.5
      p.rect({
        x: barX,
        y: 2.5,     // Center position
        width: barWidth,
        height: 2,   // Height of 2 units (from 1.5 to 3.5)
        fill_color: colors[1], // Yellow
        line_color: "#B2B2B2",
        line_width: 1
      });

      // Green segment (bottom third) - spans from y=-0.5 to y=1.5
      p.rect({
        x: barX,
        y: 0.5,     // Center position
        width: barWidth,
        height: 2,   // Height of 2 units (from -0.5 to 1.5)
        fill_color: colors[0], // Green
        line_color: "#B2B2B2",
        line_width: 1
      });

      // Add vertical text labels next to the color bar with responsive font sizes
      const legendFontSize = Math.max(plotWidth / 55, 11);

      // Label for red segment (center of red block is at y=4.5)
      p.text({
        x: barX + 0.6,
        y: 3.8,  // Exact center of red segment
        text: [`<± ${data.limits.red}`],
        text_align: "left",
        text_baseline: "middle",
        text_font_size: `${legendFontSize}px`,
        text_color: colorObject.textColor,
        angle: 1.5708  // 90 degrees in radians (Math.PI/2)
      });

      // Label for yellow segment (center of yellow block is at y=2.5)
      p.text({
        x: barX + 0.6,
        y: 1.7,  // Exact center of yellow segment
        text: [`<± ${data.limits.yellow}`],
        text_align: "left",
        text_baseline: "middle",
        text_font_size: `${legendFontSize}px`,
        text_color: colorObject.textColor,
        angle: 1.5708  // 90 degrees in radians
      });

      p.text({
        x: barX + 1,
        y: 1.7,
        text: [`[${data.unit}]`],
        text_align: "left",
        text_baseline: "middle",
        text_font_size: `${legendFontSize}px`,
        text_color: colorObject.textColor,
        angle: 1.5708
      });

      // Label for green segment (center of green block is at y=0.5)
      p.text({
        x: barX + 0.6,
        y: -0.5,  // Exact center of green segment
        text: [`<± ${data.limits.green}`],
        text_align: "left",
        text_baseline: "middle",
        text_font_size: `${legendFontSize}px`,
        text_color: colorObject.textColor,
        angle: 1.5708  // 90 degrees in radians
      });

      // Configure grid
      p.grid.grid_line_color = colorObject.backgroundColor;
      p.grid.grid_line_width = 1;

      // Tick labels for 6x6 grid
      const tick_labels = ["0.33", "0.67", "1.00", "1.33", "1.67", "2.00"];
      const tickFontSize = Math.max(plotWidth / 55, 12);

      // Add clearer, larger axis labels with responsive font sizes
      for (let i = 0; i < 6; i++) {
        // X-axis labels at the bottom
        p.text({
          x: i,
          y: -1.0,
          text: [tick_labels[i]],
          text_align: "center",
          text_baseline: "middle",
          text_font_size: `${tickFontSize}px`,
          text_color: colorObject.tickColor,
          text_font_style: "bold",
        });

        // Y-axis labels on the left - moved further to the left
        p.text({
          x: -1.1,  // Moved from -1.0 to -1.1 to prevent cutoff
          y: i,
          text: [tick_labels[i]],
          text_align: "right",
          text_baseline: "middle",
          text_font_size: `${tickFontSize}px`,
          text_color: colorObject.tickColor,
          text_font_style: "bold",
          angle: 1.5708,
        });
      }

      // Remove default ticks and their labels
      p.xaxis.major_tick_line_color = null;
      p.yaxis.major_tick_line_color = null;
      p.xaxis.minor_tick_line_color = null;
      p.yaxis.minor_tick_line_color = null;
      p.xaxis.major_label_text_color = null;
      p.yaxis.major_label_text_color = null;

      // Hide axis lines but keep the axis labels
      p.axis.visible = true;
      p.xaxis.axis_line_color = null;
      p.yaxis.axis_line_color = null;

      // The key modification - adjusted for 6x6 grid
      p.line({
        x: [-0.5, -0.5],
        y: [-0.5, 5.5], // Changed from 7.5 to 5.5 for 6x6
        line_color: null,
        line_width: 0
      });

      p.line({
        x: [-0.5, 5.5], // Changed from 7.5 to 5.5 for 6x6
        y: [-0.5, -0.5],
        line_color: null,
        line_width: 0
      });

      // Show the plot
      Bokeh_v373.Plotting.show(p, `#heatmap-${index}`);

      console.log("VO2max Error Matrix created successfully!");
    } catch (error: any) {
      console.error("Error creating heatmap:", error);
      // @ts-ignore
      document.getElementById("heatmap").innerHTML = `
      <div style="color: red; text-align: center; padding: 20px;">
        <h3>Error creating visualization</h3>
        <p>${error.message}</p>
      </div>
    `;
    }
  }
}
